import { faBookmark, faClock, faDatabase, faHospital, faHospitalUser, faSpinner, faTrashCan, faUserClock, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { Card, Col, Container, Row, Table } from "react-bootstrap";
import UplotReact from 'uplot-react';
import { ACTIVE_PATIENT_VISITS, DASHBOARD_METRICS, FAVORITES_BY_USERNAME, RECENT_PATIENT_VISITS, callQuery } from '../../amplify/appsync/query';
import { PatientVisit } from '../../amplify/appsync/search-patients';
import { DELETE_FAVORITE, callWrite } from '../../amplify/appsync/write';
import { isDemoUser } from '../../utils/demo';
import { getHourMinuteDifference, getStopWatchFormat } from '../../utils/time';
import { getUserEmail } from '../../utils/user';
import PersonalIdentifier from '../common/PersonalIdentifier';
import './Welcome.css';

/**
 * Welcome box
 */
export const Welcome = (props: {
    user: any,
    tenant: string,
    plotWidth: any,
}) => {

    const [numRecentPatients, setNumRecentPatients] = useState<any>(null);
    const [currentElapsed, setCurrentElapsed] = useState<any>(null);
    const [avgLengthOfStay, setAvgLengthOfStay] = useState<any>(null);
    const [avgDelay, setAvgDelay] = useState<any>(null);
    const [currentDelay, setCurrentDelay] = useState<any>(null);

    const [isLoadingPatients, setIsLoadingPatients] = useState<any>(false);
    const [currentPatients, setCurrentPatients] = useState<PatientVisit[]>([]);

    const [isLoadingFavoritePatients, setIsLoadingFavoritePatients] = useState<any>(false);
    const [favoritePatients, setFavoritePatients] = useState<any[]>([]);
    const [favoriteBeingDeleted, setFavoriteBeingDeleted] = useState<any>();

    const [delayData, setDelayData] = useState<any>([[], []]);
    const [delayOpts, setDelayOpts] = useState<any>();

    const [activePatients, setActivePatients] = useState<any>([[], []]);
    const [activePatientsOpts, setActivePatientsOpts] = useState<any>();

    useEffect(() => {
        setIsLoadingPatients(true);
        callQuery({
            operation: ACTIVE_PATIENT_VISITS,
        })
            .then((patients: PatientVisit[]) => {
                if (patients && patients.length > 0) {
                    patients.sort(function (a, b) {
                        if (a.bed && b.bed) {
                            const bedComparison = a.bed!.localeCompare(b.bed!);
                            if (bedComparison !== 0) {
                                return bedComparison;
                            }
                        }

                        return a.arrival_time.localeCompare(b.arrival_time);
                    });

                    const totalDifferences: any = [];
                    for (let pt of patients) {
                        // Calculate the time difference in milliseconds
                        let date2 = new Date();
                        let date1 = new Date(pt.arrival_time);
                        const timeDifference = Math.abs(date2.getTime() - date1.getTime())
                        totalDifferences.push(timeDifference);
                    }
                    const timeDifference = totalDifferences.reduce((a: number, b: number) => a + b) / totalDifferences.length;
                    setCurrentElapsed(getStopWatchFormat(timeDifference));

                    setCurrentPatients(patients);
                }
                setIsLoadingPatients(false);
            });

        callQuery({
            operation: DASHBOARD_METRICS,
        })
            .then((output: any) => {
                const delayTimestamps: any = output["monitor_data_delay"]["Timestamps"].reverse();
                const delayValues: any = output["monitor_data_delay"]["Values"].reverse();

                const msSinceLastRecievedMetrics = (new Date().getTime() - new Date(delayTimestamps[delayTimestamps.length - 1]).getTime());
                let lastKnownDelay;
                if (msSinceLastRecievedMetrics > 1000 * 60 * 20) {
                    // We expect to receive metric in 15 min increments. If we don't have data within this time frame, then
                    // system may have gone down. In this case, we use the total time.
                    lastKnownDelay = delayValues[delayValues.length - 1] * 1000 + msSinceLastRecievedMetrics;
                } else {
                    lastKnownDelay = delayValues[delayValues.length - 1] * 1000;
                }
                setCurrentDelay(getStopWatchFormat(lastKnownDelay));

                const delays: number[] = [];
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1) // get 24 hours ago
                for (let i = 0; i < delayValues.length; i++) {
                    if (new Date(delayTimestamps[i]) > yesterday) {
                        delays.push(delayValues[i]);
                    }
                }
                const avgDelay = delays.length > 0 ? delays.reduce((a: number, b: number) => a + b) / delays.length : Infinity;
                setAvgDelay(getStopWatchFormat(avgDelay * 1000));

                const finalDelayData = [
                    delayTimestamps.map((t: any) => new Date(t).getTime() / 1000),
                    delayValues.map((v: any) => v / 60),
                ];
                finalDelayData[0].push(new Date().getTime() / 1000);
                finalDelayData[1].push(null);
                setDelayData(finalDelayData);

                setDelayOpts(getDelayOpts(props.plotWidth));

                const activePatientsTimestamps: any = output["num_active_patients"]["Timestamps"].reverse();
                const activePatientsValues: any = output["num_active_patients"]["Values"].reverse();

                const finalActivePatients = [
                    activePatientsTimestamps.map((t: any) => new Date(t).getTime() / 1000),
                    activePatientsValues,
                ]
                finalActivePatients[0].push(new Date().getTime() / 1000);
                finalActivePatients[1].push(null);
                setActivePatients(finalActivePatients)

                setActivePatientsOpts(getActivePatientOpts(props.plotWidth));

            });

        if (props.tenant !== "public") {
            callQuery({
                operation: RECENT_PATIENT_VISITS,
            })
            .then((patients: PatientVisit[]) => {
                setNumRecentPatients(patients.length);

                const patientVisitLengths: number[] = [];
                for (let i = 0; i < patients.length; i++) {
                    patientVisitLengths.push((new Date(patients[i].departure_time).getTime() - new Date(patients[i].arrival_time).getTime()));
                }
                const avgStay = patientVisitLengths.reduce((a: number, b: number) => a + b) / patientVisitLengths.length

                setAvgLengthOfStay(getStopWatchFormat(avgStay));
            });
        }

        callQuery({
            operation: FAVORITES_BY_USERNAME,
            username: getUserEmail(props.user),
        })
            .then((favorites: any[]) => {
                favorites = favorites.sort((a, b) => b.favorite_time.localeCompare(a.favorite_time));
                setFavoritePatients(favorites.filter((fav: any) => fav.favorite_type === "favorite"));
                setIsLoadingFavoritePatients(false);
            });
    }, []);

    useEffect(() => {
        setDelayOpts(getDelayOpts(props.plotWidth));
        setActivePatientsOpts(getActivePatientOpts(props.plotWidth));
    }, [props.plotWidth])

    useEffect(() => {
        setDelayOpts(getDelayOpts(props.plotWidth));
    }, [delayData])

    useEffect(() => {
        setActivePatientsOpts(getActivePatientOpts(props.plotWidth));
    }, [activePatients])

    function tooltipPlugin(title: string, isInt: boolean) {
        let seriestt: any;

        function init(u: any, opts: any, data: any) {
            let over = u.over;

            seriestt = document.createElement("div");
            seriestt.className = "u-tooltip";
            seriestt.textContent = "";
            seriestt.style.pointerEvents = "none";
            seriestt.style.position = "absolute";
            over.appendChild(seriestt);

            function hideTips() {
                seriestt.style.display = "none";
                seriestt.style.opacity = 0;
            }

            function showTips() {
                seriestt.style.display = "block";
                seriestt.style.opacity = 1;
            }

            over.addEventListener("mouseleave", () => {
                hideTips();
            });

            over.addEventListener("mouseenter", () => {
                showTips();
            });
        }

        function setCursor(u: any) {
            const {left, top, idx} = u.cursor;

            let xVal = u.data[0][idx];
            let yVal = u.data[1][idx]
            if (yVal) {
                if (isInt) {
                    yVal = Math.round(yVal);
                } else {
                    yVal = yVal.toFixed(2);
                }
            }

            const dt = new Date(xVal * 1000)
            if (!isNaN(dt.valueOf())) {
                seriestt.innerHTML = `
                    <div class='u-tooltip-header'>${dt.toLocaleString()}</div>
                    <div class='u-tooltip-body'>
                        ${yVal}
                    </div>
                `
                seriestt.style.left = Math.round(u.valToPos(xVal, 'x') + 10) + "px";
                seriestt.style.top = Math.round(u.valToPos(yVal, 'y') + 10) + "px";
            }
        }

        return {
            hooks: {
                init,
                setCursor,
            },
        };
    }

    const getDelayOpts = (plotWidth: number) => {
        return {
            width: plotWidth,
            height: 230,
            legend: {
                show: false
            },
            scales: {
                x: {
                    time: true,
                },
            },
            series: [{},
            {
                stroke: "#1F3D96",
            }
            ],
            axes: [
                {},
                {
                    label: "Minutes",
                    labelGap: 8,
                }
            ],
            plugins: [
                tooltipPlugin("Delay (min)", false),
            ],
        }
    }

    const getActivePatientOpts = (plotWidth: number) => {
        return {
            width: plotWidth,
            height: 230,
            legend: {
                show: false
            },
            scales: {
                x: {
                    time: true,
                },
            },
            series: [{},
            {
                stroke: "#1F3D96",
            }
            ],
            axes: [
                {},
                {
                    label: "Patients",
                    labelGap: 8,
                }
            ],
            plugins: [
                tooltipPlugin("Active Patients", true),
            ],
        }
    }

    const deleteFavorite = (favoriteId: string) => {
        setFavoriteBeingDeleted(favoriteId);
        callWrite({
            operation: DELETE_FAVORITE,
            favorite_id: favoriteId,
        })
            .then((output: any) => {
                setFavoriteBeingDeleted(null);
            });
    }

    return (
        <Container fluid style={{ marginTop: isDemoUser(props.user) ? '151px' : '72px' }}>
            <Row className="my-3">
                <Col>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                        <Card.Body>
                            <Row>
                                <Col className="text-center">
                                    <Row>
                                        <Col className="welcome-info-box-title">
                                            <FontAwesomeIcon icon={faUsers} className='welcome-info-box-icon'/> # of Patient (24 Hours)
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col className="welcome-info-box-metric">
                                            {
                                                props.tenant === "public" ? 0 : numRecentPatients
                                            }
                                        </Col>
                                    </Row>
                                    {/* <Row>
                                        <Col className="welcome-info-box-relative">
                                            <Badge bg="secondary"><FontAwesomeIcon icon={faArrowDown} />{' '}12% from yesterday</Badge>
                                        </Col>
                                    </Row> */}
                                </Col>
                                <></>
                                <Col className="text-center" style={{borderLeft: '1px solid #dedede'}}>
                                    <Row>
                                        <Col className="welcome-info-box-title">
                                            <FontAwesomeIcon icon={faUserClock} className='welcome-info-box-icon'/> Average Length of Stay (24 hours)
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col className="welcome-info-box-metric">
                                            {
                                                props.tenant === "public" ? 0 : avgLengthOfStay
                                            }
                                        </Col>
                                    </Row>
                                    {/* <Row>
                                        <Col className="welcome-info-box-relative">
                                            <Badge bg="secondary"><FontAwesomeIcon icon={faArrowUp} />{' '}12% from yesterday</Badge>
                                        </Col>
                                    </Row> */}
                                </Col>
                                <Col className="text-center" style={{borderLeft: '1px solid #dedede'}}>
                                    <Row>
                                        <Col className="welcome-info-box-title">
                                            <FontAwesomeIcon icon={faHospital} className='welcome-info-box-icon'/> Avg ED Time (Current Patients)
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col className="welcome-info-box-metric">
                                            {
                                                props.tenant === "public" ? "00:00" : currentElapsed
                                            }
                                        </Col>
                                    </Row>
                                    {/* <Row>
                                        <Col className="welcome-info-box-relative">
                                            <Badge bg="secondary"><FontAwesomeIcon icon={faArrowUp} />{' '}12% from yesterday</Badge>
                                        </Col>
                                    </Row> */}
                                </Col>
                                <Col className="text-center" style={{borderLeft: '1px solid #dedede'}}>
                                    <Row>
                                        <Col className="welcome-info-box-title">
                                            <FontAwesomeIcon icon={faDatabase} className='welcome-info-box-icon'/> Average Data Delay (24 hours)
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col className="welcome-info-box-metric">
                                            {
                                                props.tenant === "public" ? "00:00" : avgDelay
                                            }
                                        </Col>
                                    </Row>
                                    {/* <Row>
                                        <Col className="welcome-info-box-relative">
                                            <Badge bg="primary"><FontAwesomeIcon icon={faArrowUp} />{' '}12% from yesterday</Badge>
                                        </Col>
                                    </Row> */}
                                </Col>
                                <Col className="text-center" style={{borderLeft: '1px solid #dedede'}}>
                                    <Row>
                                        <Col className="welcome-info-box-title">
                                            <FontAwesomeIcon icon={faClock} className='welcome-info-box-icon'/> Current Data Delay
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col className="welcome-info-box-metric">
                                            {
                                                props.tenant === "public" ? "00:00" : currentDelay
                                            }
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row className="my-3">
                <Col md={12}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <h6 className="graph-card-title">
                                <FontAwesomeIcon icon={faClock} className='chart-title-icon'/> Current Data Delay
                            </h6>
                            {
                                delayData.length > 0 && delayOpts && Object.keys(delayOpts).length > 0 && (
                                    <UplotReact
                                        options={delayOpts}
                                        data={delayData as uPlot.AlignedData}
                                    />
                                )
                            }
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row className="my-3">
                <Col md={12}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <h6 className="graph-card-title">
                                <FontAwesomeIcon icon={faUsers} className='chart-title-icon'/> Number of Active Patients
                            </h6>
                            {
                                activePatients.length > 0 && activePatientsOpts && Object.keys(activePatientsOpts).length > 0 && (
                                    <UplotReact
                                        options={activePatientsOpts}
                                        data={activePatients as uPlot.AlignedData}
                                    />
                                )
                            }
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row className="my-3">
                <Col md={8}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <h5 className="card-title"><FontAwesomeIcon icon={faHospitalUser} className='welcome-info-box-icon'/> Current Patients{' '}{isLoadingPatients ? (
                                <><FontAwesomeIcon icon={faSpinner} spin /></>
                            ) : (
                                <>({currentPatients.length})</>
                            )}</h5>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>CSN</th>
                                        <th>Bed</th>
                                        <th>Chief Complaint</th>
                                        <th>Elapsed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        currentPatients.map((patient) => (
                                            <tr key={patient.csn}>
                                                <td><a href={`/dashboard/${patient.internal_csn}`} target="_blank"><PersonalIdentifier identifier={patient.csn} /></a></td>
                                                <td>{patient.bed}</td>
                                                <td>{patient.chief_complaint}</td>
                                                <td>{getHourMinuteDifference(new Date(patient.arrival_time), new Date())}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <h5 className="card-title"><FontAwesomeIcon icon={faBookmark} className='welcome-info-box-icon'/> Favorites{' '}{isLoadingFavoritePatients ? (
                                <><FontAwesomeIcon icon={faSpinner} spin /></>
                            ) : (
                                <>({favoritePatients.length})</>
                            )}</h5>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>CSN</th>
                                        {/* <th>Bed</th>
                                        <th>Chief Complaint</th>
                                        <th>Elapsed</th> */}
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        favoritePatients.map((fav) => (
                                            <tr key={fav.csn}>
                                                <td><a href={`/dashboard/${fav.internal_csn}`} target={isDemoUser(props.user) ? "" : "_blank"}><PersonalIdentifier identifier={fav.csn} /></a></td>
                                                {/* <td>{patient.bed}</td>
                                                <td>{patient.chief_complaint}</td> */}

                                                <td className="text-center">
                                                    <a href="#" onClick={() => { deleteFavorite(fav.favorite_id) }} target={isDemoUser(props.user) ? "" : "_blank"}>
                                                        {
                                                            favoriteBeingDeleted === fav.favorite_id ? (
                                                                <FontAwesomeIcon icon={faSpinner} spin />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faTrashCan} />
                                                            )
                                                        }
                                                    </a>
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};
